import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { FileText, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function TemplatePicker({ onSelect, testId = "template-picker" }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/templates"); setTemplates(data); } catch { /* noop */ }
    })();
  }, [open]);

  const filtered = templates.filter(t =>
    !q ||
    t.name.toLowerCase().includes(q.toLowerCase()) ||
    t.body.toLowerCase().includes(q.toLowerCase()) ||
    (t.category || "").toLowerCase().includes(q.toLowerCase())
  );

  const grouped = filtered.reduce((acc, t) => {
    const c = t.category || "General";
    (acc[c] = acc[c] || []).push(t);
    return acc;
  }, {});

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid={testId}>
          <FileText className="w-3.5 h-3.5 mr-2 text-[#D9232E]" /> Templates
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid={`${testId}-content`}>
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates..." className="pl-8 h-9 bg-slate-50 border-slate-200" />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto thin-scroll">
          {Object.keys(grouped).length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">No templates found</div>
          )}
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="px-3 py-1.5 bg-slate-50 border-y border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">{cat}</div>
              {items.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onSelect(t.body); setOpen(false); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-red-50 border-b border-slate-100 transition-colors"
                  data-testid={`template-${t.id}`}
                >
                  <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{t.body}</div>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
